/**
 * Per-plugin credential forms (PR-4b).
 *
 * Each form is TS-typed against the plugin's pydantic credential
 * schema. The backend re-validates with the plugin's
 * ``credential_schema`` so the schema stays the single source of
 * truth — a typo here surfaces as a server-side 400
 * ``invalid_credentials`` with the offending field name in
 * ``detail``.
 *
 * Two modes:
 *
 * - ``mode="create"``: every field (secret + config) is rendered.
 *   The form submits a full ``credentials`` bag for
 *   ``POST /provider-accounts/``.
 * - ``mode="rotate"``: only the secret-classified fields are rendered.
 *   Submitted to ``POST /provider-accounts/{id}/rotate-secrets/``.
 *
 * Adding a new plugin: write a new ``XxxForm`` here + register it
 * in ``PLUGIN_FORMS`` at the bottom + add the option to
 * ``PLUGIN_OPTIONS``. Backend changes (the plugin's pydantic schema)
 * govern correctness; this file governs UX.
 */
import { type ReactNode, useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export type PluginFormMode = "create" | "rotate";

export interface PluginFormProps {
  mode: PluginFormMode;
  /** Whether the parent's mutation is in flight; disables inputs. */
  submitting: boolean;
  /** Submit handler — receives the credentials bag. */
  onSubmit: (credentials: Record<string, string>) => void;
  /** Cancel handler. */
  onCancel: () => void;
}

function FieldRow({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function FormFooter({
  submitting,
  onCancel,
  submitLabel,
}: {
  submitting: boolean;
  onCancel: () => void;
  submitLabel: string;
}) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
        Cancel
      </Button>
      <Button type="submit" disabled={submitting} data-testid="provider-form-submit">
        {submitting ? "Saving…" : submitLabel}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------
// sasapay
// ---------------------------------------------------------------------

export function SasaPayForm({ mode, submitting, onSubmit, onCancel }: PluginFormProps) {
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [callbackSecret, setCallbackSecret] = useState("");
  const [environment, setEnvironment] = useState<"sandbox" | "production">("sandbox");
  const [merchantCode, setMerchantCode] = useState("");
  const [paybillNumber, setPaybillNumber] = useState("");
  const [callbackPathToken, setCallbackPathToken] = useState("");
  const [b2cChannel, setB2cChannel] = useState("0");

  function submit(e: FormEvent) {
    e.preventDefault();
    const creds: Record<string, string> = {
      client_id: clientId.trim(),
      client_secret: clientSecret.trim(),
    };
    if (callbackSecret.trim()) creds.callback_secret = callbackSecret.trim();
    if (mode === "create") {
      creds.environment = environment;
      creds.merchant_code = merchantCode.trim();
      creds.paybill_number = paybillNumber.trim();
      if (callbackPathToken.trim()) {
        creds.callback_path_token = callbackPathToken.trim();
      }
      creds.b2c_channel = b2cChannel.trim() || "0";
    }
    onSubmit(creds);
  }

  return (
    <form className="space-y-4" onSubmit={submit} noValidate>
      <FieldRow label="Client ID" htmlFor="sp-client-id" hint="From SasaPay developer dashboard.">
        <Input
          id="sp-client-id"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          required
          disabled={submitting}
        />
      </FieldRow>
      <FieldRow label="Client secret" htmlFor="sp-client-secret">
        <Input
          id="sp-client-secret"
          type="password"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          required
          disabled={submitting}
        />
      </FieldRow>
      <FieldRow
        label="Callback signing secret (optional)"
        htmlFor="sp-callback-secret"
        hint="SasaPay does not sign callbacks. Set only if you're putting a signed reverse-proxy in front of the URL."
      >
        <Input
          id="sp-callback-secret"
          type="password"
          value={callbackSecret}
          onChange={(e) => setCallbackSecret(e.target.value)}
          disabled={submitting}
        />
      </FieldRow>

      {mode === "create" && (
        <>
          <FieldRow label="Environment" htmlFor="sp-env">
            <select
              id="sp-env"
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as typeof environment)}
              disabled={submitting}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="sandbox">sandbox</option>
              <option value="production">production</option>
            </select>
          </FieldRow>
          <FieldRow label="Merchant code" htmlFor="sp-mrc">
            <Input
              id="sp-mrc"
              value={merchantCode}
              onChange={(e) => setMerchantCode(e.target.value)}
              required
              disabled={submitting}
            />
          </FieldRow>
          <FieldRow label="Paybill number" htmlFor="sp-paybill">
            <Input
              id="sp-paybill"
              value={paybillNumber}
              onChange={(e) => setPaybillNumber(e.target.value)}
              required
              disabled={submitting}
            />
          </FieldRow>
          <FieldRow
            label="Callback path token (optional)"
            htmlFor="sp-token"
            hint="Leave blank — we'll auto-mint a high-entropy URL token (~256 bits). Supply your own only if you have a specific reason."
          >
            <Input
              id="sp-token"
              value={callbackPathToken}
              onChange={(e) => setCallbackPathToken(e.target.value)}
              disabled={submitting}
            />
          </FieldRow>
          <FieldRow
            label="B2C channel"
            htmlFor="sp-channel"
            hint="0 = SasaPay wallet (default); 63902 = M-PESA; 63903 = AirtelMoney; 63907 = T-Kash."
          >
            <Input
              id="sp-channel"
              value={b2cChannel}
              onChange={(e) => setB2cChannel(e.target.value)}
              disabled={submitting}
            />
          </FieldRow>
        </>
      )}

      <FormFooter
        submitting={submitting}
        onCancel={onCancel}
        submitLabel={mode === "create" ? "Create provider account" : "Rotate secrets"}
      />
    </form>
  );
}

// ---------------------------------------------------------------------
// mpesa_daraja
// ---------------------------------------------------------------------

export function MpesaDarajaForm({
  mode,
  submitting,
  onSubmit,
  onCancel,
}: PluginFormProps) {
  const [consumerKey, setConsumerKey] = useState("");
  const [consumerSecret, setConsumerSecret] = useState("");
  const [passkey, setPasskey] = useState("");
  const [initiatorName, setInitiatorName] = useState("");
  // Spec 0010 §5.6.1: the operator types the initiator password and
  // Senti encrypts it server-side, OR pastes a credential encrypted
  // offline. Mutually exclusive: only the chosen one is sent.
  const [credentialMode, setCredentialMode] = useState<"password" | "encrypted">(
    "password",
  );
  const [initiatorPassword, setInitiatorPassword] = useState("");
  const [securityCredential, setSecurityCredential] = useState("");
  const [callbackSecret, setCallbackSecret] = useState("");
  const [environment, setEnvironment] = useState<"sandbox" | "production">("sandbox");
  const [businessShortCode, setBusinessShortCode] = useState("");
  const [transactionType, setTransactionType] = useState<
    "CustomerPayBillOnline" | "CustomerBuyGoodsOnline"
  >("CustomerPayBillOnline");
  const [b2cCommandId, setB2cCommandId] = useState<
    "BusinessPayment" | "SalaryPayment" | "PromotionPayment"
  >("BusinessPayment");
  const [callbackPathToken, setCallbackPathToken] = useState("");

  function submit(e: FormEvent) {
    e.preventDefault();
    const creds: Record<string, string> = {};
    // Rotate merges over the stored secrets, so a blank field there means
    // "keep it" and is not sent (the server refuses empty strings).
    if (mode === "create" || consumerKey.trim()) creds.consumer_key = consumerKey.trim();
    if (mode === "create" || consumerSecret.trim()) {
      creds.consumer_secret = consumerSecret.trim();
    }
    if (passkey.trim()) creds.passkey = passkey.trim();
    if (initiatorName.trim()) creds.initiator_name = initiatorName.trim();
    // The password is sent verbatim (never trimmed: a space may be part
    // of it). The server encrypts it and never stores it.
    if (credentialMode === "password") {
      if (initiatorPassword) creds.initiator_password = initiatorPassword;
    } else if (securityCredential.trim()) {
      creds.security_credential = securityCredential.trim();
    }
    if (callbackSecret.trim()) creds.callback_secret = callbackSecret.trim();
    if (mode === "create") {
      creds.environment = environment;
      creds.business_short_code = businessShortCode.trim();
      creds.transaction_type = transactionType;
      creds.b2c_command_id = b2cCommandId;
      if (callbackPathToken.trim()) {
        creds.callback_path_token = callbackPathToken.trim();
      }
    }
    onSubmit(creds);
  }

  return (
    <form className="space-y-4" onSubmit={submit} noValidate>
      <FieldRow label="Consumer key" htmlFor="dr-ck">
        <Input
          id="dr-ck"
          value={consumerKey}
          onChange={(e) => setConsumerKey(e.target.value)}
          required={mode === "create"}
          disabled={submitting}
        />
      </FieldRow>
      <FieldRow label="Consumer secret" htmlFor="dr-cs">
        <Input
          id="dr-cs"
          type="password"
          value={consumerSecret}
          onChange={(e) => setConsumerSecret(e.target.value)}
          required={mode === "create"}
          disabled={submitting}
        />
      </FieldRow>
      <FieldRow
        label="Passkey (STK push — required for collect)"
        htmlFor="dr-pk"
        hint="Supplied by Safaricom per shortcode. Empty for B2C-only accounts."
      >
        <Input
          id="dr-pk"
          type="password"
          value={passkey}
          onChange={(e) => setPasskey(e.target.value)}
          disabled={submitting}
        />
      </FieldRow>
      <FieldRow
        label="Initiator name (B2C only)"
        htmlFor="dr-init"
        hint="Empty for collect-only accounts."
      >
        <Input
          id="dr-init"
          value={initiatorName}
          onChange={(e) => setInitiatorName(e.target.value)}
          disabled={submitting}
        />
      </FieldRow>
      <fieldset className="space-y-2" disabled={submitting}>
        <legend className="text-sm font-medium">Initiator credential (B2C / B2B only)</legend>
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="dr-credential-mode"
              value="password"
              checked={credentialMode === "password"}
              onChange={() => {
                setCredentialMode("password");
                setSecurityCredential("");
              }}
              data-testid="dr-mode-password"
            />
            Initiator password (Senti encrypts it)
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="dr-credential-mode"
              value="encrypted"
              checked={credentialMode === "encrypted"}
              onChange={() => {
                setCredentialMode("encrypted");
                setInitiatorPassword("");
              }}
              data-testid="dr-mode-encrypted"
            />
            Pre-encrypted security credential
          </label>
        </div>
        {credentialMode === "password" ? (
          <FieldRow
            label="Initiator password"
            htmlFor="dr-ipw"
            hint="Senti encrypts it with Safaricom's certificate for this account's environment and stores only the result. The password itself is never stored, logged or shown again. Empty for collect-only accounts."
          >
            <Input
              id="dr-ipw"
              type="password"
              autoComplete="off"
              value={initiatorPassword}
              onChange={(e) => setInitiatorPassword(e.target.value)}
              disabled={submitting}
            />
          </FieldRow>
        ) : (
          <FieldRow
            label="Security credential"
            htmlFor="dr-sc"
            hint="Already RSA-encrypted offline, e.g. with python -m senti_provider_mpesa_daraja.security_credential."
          >
            <Input
              id="dr-sc"
              type="password"
              autoComplete="off"
              value={securityCredential}
              onChange={(e) => setSecurityCredential(e.target.value)}
              disabled={submitting}
            />
          </FieldRow>
        )}
      </fieldset>
      <FieldRow
        label="Callback signing secret (optional)"
        htmlFor="dr-cbsec"
        hint="Daraja does not sign callbacks. Set only if you're putting a signed reverse-proxy in front."
      >
        <Input
          id="dr-cbsec"
          type="password"
          value={callbackSecret}
          onChange={(e) => setCallbackSecret(e.target.value)}
          disabled={submitting}
        />
      </FieldRow>

      {mode === "create" && (
        <>
          <FieldRow label="Environment" htmlFor="dr-env">
            <select
              id="dr-env"
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as typeof environment)}
              disabled={submitting}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="sandbox">sandbox</option>
              <option value="production">production</option>
            </select>
          </FieldRow>
          <FieldRow label="Business short code" htmlFor="dr-bsc">
            <Input
              id="dr-bsc"
              value={businessShortCode}
              onChange={(e) => setBusinessShortCode(e.target.value)}
              required
              disabled={submitting}
            />
          </FieldRow>
          <FieldRow
            label="Transaction type"
            htmlFor="dr-tt"
            hint="Must match the till type Safaricom provisioned the short code as. Picking the wrong one silently round-trips STK pushes that fail at first live transaction."
          >
            <select
              id="dr-tt"
              value={transactionType}
              onChange={(e) =>
                setTransactionType(e.target.value as typeof transactionType)
              }
              disabled={submitting}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="CustomerPayBillOnline">CustomerPayBillOnline (paybill)</option>
              <option value="CustomerBuyGoodsOnline">CustomerBuyGoodsOnline (till)</option>
            </select>
          </FieldRow>
          <FieldRow
            label="B2C command ID"
            htmlFor="dr-b2c"
            hint="BusinessPayment is the right choice for loan disbursements."
          >
            <select
              id="dr-b2c"
              value={b2cCommandId}
              onChange={(e) => setB2cCommandId(e.target.value as typeof b2cCommandId)}
              disabled={submitting}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="BusinessPayment">BusinessPayment</option>
              <option value="SalaryPayment">SalaryPayment</option>
              <option value="PromotionPayment">PromotionPayment</option>
            </select>
          </FieldRow>
          <FieldRow
            label="Callback path token (optional)"
            htmlFor="dr-token"
            hint="Leave blank — auto-minted high-entropy URL token."
          >
            <Input
              id="dr-token"
              value={callbackPathToken}
              onChange={(e) => setCallbackPathToken(e.target.value)}
              disabled={submitting}
            />
          </FieldRow>
        </>
      )}

      <FormFooter
        submitting={submitting}
        onCancel={onCancel}
        submitLabel={mode === "create" ? "Create provider account" : "Rotate secrets"}
      />
    </form>
  );
}

// ---------------------------------------------------------------------
// fake — kept TS-typed for test parity with the backend.
// ---------------------------------------------------------------------

export function FakeForm({ mode, submitting, onSubmit, onCancel }: PluginFormProps) {
  const [apiKey, setApiKey] = useState("");

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({ api_key: apiKey.trim() });
  }

  return (
    <form className="space-y-4" onSubmit={submit} noValidate>
      <FieldRow
        label="API key"
        htmlFor="fk-key"
        hint="Test-only plugin (senti-provider-fake). Useful for E2E rehearsals."
      >
        <Input
          id="fk-key"
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          required
          disabled={submitting}
        />
      </FieldRow>
      <FormFooter
        submitting={submitting}
        onCancel={onCancel}
        submitLabel={mode === "create" ? "Create provider account" : "Rotate secrets"}
      />
    </form>
  );
}

// ---------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------

export interface PluginOption {
  value: string;
  label: string;
  description: string;
  form: (props: PluginFormProps) => ReactNode;
}

export const PLUGIN_OPTIONS: ReadonlyArray<PluginOption> = [
  {
    value: "mpesa_daraja",
    label: "M-Pesa Daraja",
    description: "Safaricom M-Pesa via the Daraja API.",
    form: (props) => <MpesaDarajaForm {...props} />,
  },
  {
    value: "sasapay",
    label: "SasaPay",
    description: "Cross-network mobile money + bank rails via SasaPay.",
    form: (props) => <SasaPayForm {...props} />,
  },
  {
    value: "fake",
    label: "Fake (test only)",
    description: "Records calls in-process. Useful for E2E rehearsals.",
    form: (props) => <FakeForm {...props} />,
  },
] as const;

export function getPluginOption(value: string): PluginOption | undefined {
  return PLUGIN_OPTIONS.find((opt) => opt.value === value);
}
